import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import { css } from '@emotion/react';
import FormElement from '../../FormElement';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { IoAlertCircleOutline } from 'react-icons/io5';
import Swal from 'sweetalert2';
export const makeFieldDataUnique = (field, rowId) => {
    if (!field.data || !Array.isArray(field.data)) return field;
    if (['Checkbox', 'Radio', 'Select'].includes(field.key)) {
        return {
            ...field,
            data: field.data.map((option) => ({
                ...option,
                value: `row_${rowId}__${option.value}`,
                originalValue: option.value,
            })),
        };
    }

    return field;
};

export const processFieldValue = (fieldData, processedField, found) => {
    if (fieldData.value == null) return '';

    return typeof fieldData.value === 'object' ? JSON.stringify(fieldData.value) : fieldData.value;
};

const getInitialValueForField = (field, newRow) => {
    if (!newRow && field.value !== undefined && field.value !== '') return field.value;

    switch (field.key) {
        case 'FullName':
            return JSON.stringify({ firstName: '', lastName: '' });
        case 'Checkbox':
            return JSON.stringify([]);
        case 'Select':
            return field.data && field.data.length > 0 ? field.data[0].value : '';
        case 'DatePicker':
        case 'TimePicker':
        case 'DateTimePicker':
            return new Date().toISOString();
        default:
            return '';
    }
};

const Component = ({
    data,
    elementRef,
    error,
    errors,
    isPreview,
    isDraft,
    currUser,
    setError,
    clearErrors,
    setValue,
}) => {
    const defaultValues = useMemo(() => {
        try {
            const parsed = JSON.parse(data.value || '[]');
            const rowsObject = {};

            if (Array.isArray(parsed)) {
                parsed.forEach((row) => {
                    if (row && row.length > 0 && row[0]?.fieldName) {
                        const match = row[0].fieldName.match(/^(.+?)__row_(.+?)__(.+)$/);
                        if (match) {
                            const [, , rowId] = match;
                            if (rowId) {
                                rowsObject[rowId] = row;
                            }
                        }
                    }
                });
            }
            return rowsObject;
        } catch {
            return {};
        }
    }, [data?.value]);
    const [fieldErrors, setFieldErrors] = useState({});
    const _minRows = Number(data?.fieldProperties?.minRows ?? 0) || 0;
    const minRows = data?.required ? Math.max(_minRows, 1) : _minRows;
    const maxRows = Number(data?.fieldProperties?.maxRows ?? 100) || 100;
    const makeRowFromDef = useCallback((fieldsDef, rowId = Date.now().toString()) => {
        return {
            rowId,
            fields: Array.isArray(fieldsDef) ? fieldsDef.map((field) => makeFieldDataUnique(field, rowId)) : [],
        };
    }, []);

    const initialRows = useMemo(() => {
        const rows = {};
        if (Object.keys(defaultValues).length > 0) {
            Object.entries(defaultValues).forEach(([rowId, rawRow]) => {
                const fieldsWithValues = (rawRow || []).map((fieldData) => {
                    const match = (fieldData.fieldName || '').match(/^(.+?)__row_(.+?)__(.+)$/);
                    const found = match ? (data.data || []).find((f) => f._id === match[3]) || {} : {};
                    let processedField = makeFieldDataUnique(found, rowId);
                    const value = processFieldValue(fieldData, processedField, found);

                    processedField.value = value;
                    return {
                        ...processedField,
                        originalFieldName: fieldData.fieldName,
                    };
                });

                rows[rowId] = {
                    rowId,
                    fields: fieldsWithValues,
                };
            });
        }

        const currentRowCount = Object.keys(rows).length;
        if (currentRowCount < minRows) {
            for (let i = currentRowCount; i < minRows; i++) {
                const rowId = (Date.now() + i).toString();
                const newRow = makeRowFromDef(data?.data || [], rowId);
                newRow.fields = newRow.fields.map((field) => {
                    return {
                        ...field,
                        value: getInitialValueForField(field),
                    };
                });
                rows[rowId] = newRow;
            }
        }

        return rows;
    }, [defaultValues, data?.data, data.data?.map((f) => JSON.stringify(f)).join(','), minRows, makeRowFromDef]);

    const [rows, setRows] = useState(initialRows);
    useEffect(() => {
        if (Object.keys(rows).length === 0 || isPreview) {
            setRows(initialRows);
        }
    }, [initialRows]);

    useEffect(() => {
        const rowCount = Object.keys(rows).length;
        if (!isDraft && (data?.required ?? false) && rowCount < 1) {
            setError(`ConfigurableList-${data._id}`, {
                type: 'required',
                message: 'This field is required',
            });
        } else {
            clearErrors(`ConfigurableList-${data._id}`);
        }
    }, [Object.keys(rows).length, data?._id, data?.required, isDraft]);

    const sortedRowIds = useMemo(() => {
        return Object.keys(rows).sort((a, b) => parseInt(a) - parseInt(b));
    }, [rows]);

    const validateRows = useCallback(
        (value) => {
            setFieldErrors({});
            if (isDraft) return true;
            try {
                for (let i = 0; i < sortedRowIds.length; i++) {
                    const rowId = sortedRowIds[i];
                    const row = rows[rowId];
                    for (const field of row.fields) {
                        if (field.required) {
                            let isEmpty = false;
                            if (field.key === 'FullName') {
                                try {
                                    const val = JSON.parse(field.value);
                                    if (!val.firstName || !val.lastName) isEmpty = true;
                                } catch (e) {
                                    isEmpty = true;
                                }
                            } else if (field.key === 'Checkbox') {
                                try {
                                    const val = JSON.parse(field.value);
                                    if (!Array.isArray(val) || val.length === 0) isEmpty = true;
                                } catch (e) {
                                    isEmpty = true;
                                }
                            } else {
                                if (!field.value) isEmpty = true;
                            }

                            if (isEmpty) {
                                if (field.key === 'FullName') {
                                    setFieldErrors({
                                        [`${field?.key}-${data._id}__row_${rowId}__${field._id}`]: {
                                            firstName: {
                                                type: 'required',
                                            },
                                            lastName: {
                                                type: 'required',
                                            },
                                        },
                                    });
                                } else {
                                    setFieldErrors({
                                        [`${field?.key}-${data._id}__row_${rowId}__${field._id}`]: { type: 'required' },
                                    });
                                }
                                return `This field is required: "${field.fieldName || field.name || field.key}" in "Row ${i + 1}".`;
                            }
                        }
                    }
                }
                return true;
            } catch (e) {
                return true;
            }
        },
        [rows, isDraft, sortedRowIds]
    );

    const getFormattedValue = useCallback(() => {
        return JSON.stringify(
            Object.values(rows).map((row) =>
                row.fields.map((field) => {
                    let value = field.value;
                    if (['Checkbox', 'FullName'].includes(field.key) && typeof value === 'string') {
                        try {
                            value = JSON.parse(value);
                        } catch (e) {
                            // keep original value if parse fails
                        }
                    }

                    const fieldName = `${data._id}__row_${row.rowId}__${field._id}`;

                    return {
                        value,
                        fieldName,
                    };
                })
            )
        );
    }, [rows, data._id]);

    // Update form value when rows change
    useEffect(() => {
        if (setValue) {
            setValue(`ConfigurableList-${data._id}`, getFormattedValue());
        }
    }, [rows, setValue, data._id, getFormattedValue]);

    const handleFieldChange = useCallback((rowId, fieldId, newValue) => {
        setRows((prev) => {
            const row = prev[rowId];
            if (!row) return prev;

            const newFields = row.fields.map((field) => {
                if (field._id === fieldId) {
                    return { ...field, value: newValue };
                }
                return field;
            });

            return {
                ...prev,
                [rowId]: { ...row, fields: newFields },
            };
        });
    }, []);

    const handleAddRow = useCallback(() => {
        setRows((prev) => {
            if (Object.keys(prev).length >= maxRows) return prev;
            const rowId = Date.now().toString();
            const newRow = makeRowFromDef(data?.data || [], rowId);
            newRow.fields = newRow.fields.map((field) => {
                return {
                    ...field,
                    value: getInitialValueForField(field, true),
                };
            });
            return { ...prev, [rowId]: newRow };
        });
    }, [maxRows, makeRowFromDef, data?.data]);

    const handleRemoveRow = useCallback(
        async (rowId) => {
            const result = await Swal.fire({
                title: `Are you sure you want to delete ?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
            });

            if (result.isConfirmed) {
                setRows((prev) => {
                    if (Object.keys(prev).length <= minRows) return prev;
                    const newRows = { ...prev };
                    delete newRows[rowId];
                    return newRows;
                });
            }
        },
        [minRows]
    );

    return (
        <div
            css={css`
                margin-bottom: 2rem;
                padding: 1rem;
                .row {
                    margin-left: 0px;
                    margin-right: 0px;
                }
            `}
        >
            <Form.Label>
                {data.fieldName} <span style={{ color: 'red' }}>{(data.required ?? true) && '*'}</span>
            </Form.Label>
            {data.instruction && (
                <Form.Text
                    className="small text-muted warpIt preStyles"
                    css={css`
                        margin-bottom: 0 !important;
                    `}
                >
                    {data.instruction}
                </Form.Text>
            )}
            {sortedRowIds.map((rowId, rowIndex) => {
                const row = rows[rowId];
                return (
                    <Row
                        key={rowId}
                        css={css`
                            border: 1px solid #ddd;
                            border-radius: 6px;
                            margin-bottom: 1rem;
                            background-color: ${rowIndex % 2 === 0 ? '#fafafa' : '#fff'};
                            padding: 1rem;
                        `}
                    >
                        <div
                            css={css`
                                display: inline-flex;
                                align-items: center;
                                background: #f8f9fa;
                                border: 1px solid #dadce0;
                                color: #5f6368;
                                padding: 4px 12px;
                                border-radius: 16px;
                                font-size: 14px;
                                font-weight: 500;
                                margin-bottom: 0.5rem;
                                margin-left: 14px;
                            `}
                        >
                            Row #{rowIndex + 1}
                        </div>
                        <br />
                        {Array.isArray(row.fields) &&
                            row.fields.map((field) => {
                                const scopedId = `${data._id}__row_${rowId}__${field._id}`;
                                const fieldScoped = {
                                    ...field,
                                    required: isDraft ? false : data?.required ? field?.required || false : false,
                                    _id: scopedId,
                                    id: scopedId,
                                    name: scopedId,
                                };

                                return (
                                    <Col key={scopedId} md={12}>
                                        <FormElement
                                            form={fieldScoped}
                                            // Pass no-op ref to prevent child registration
                                            elementRef={() => {}}
                                            errors={fieldErrors}
                                            isPreview={isPreview}
                                            currUser={currUser}
                                            onChange={(val) => handleFieldChange(rowId, field._id, val)}
                                        />
                                    </Col>
                                );
                            })}
                        {!isPreview && sortedRowIds.length > minRows && (
                            <div
                                css={css`
                                    text-align: right;
                                    width: 100%;
                                    margin-top: 0.5rem;
                                `}
                            >
                                <Button
                                    variant="outline-danger"
                                    onClick={() => handleRemoveRow(rowId)}
                                    style={{ fontSize: '1.4rem' }}
                                >
                                    Remove Row
                                </Button>
                            </div>
                        )}
                    </Row>
                );
            })}

            {!isPreview && sortedRowIds.length < maxRows && (
                <Button variant="outline-primary" onClick={handleAddRow} style={{ fontSize: '1.4rem' }}>
                    + Add Row
                </Button>
            )}
            <input
                style={{
                    opacity: 0,
                    width: '1px',
                    height: '1px',
                    position: 'absolute',
                    pointerEvents: 'none',
                    zIndex: -1,
                }}
                name={`ConfigurableList-${data._id}`}
                ref={
                    elementRef &&
                    elementRef({
                        required: !isDraft && data.required ? 'This field is required' : false,
                        validate: validateRows,
                    })
                }
                value={getFormattedValue()}
                readOnly
            />
            <Form.Text className="text-danger">
                {(error?.type === 'required' || error?.message) && (
                    <IoAlertCircleOutline size={18} color="#c93535" className="mr-1" />
                )}
                {error && (error.type === 'required' ? error.message || 'This field is required.' : error.message)}
            </Form.Text>
        </div>
    );
};

const ConfigurableList = (props) => {
    return (
        <ComponentErrorBoundary error="Failed to load Configurable_List">
            <Component {...props} />
        </ComponentErrorBoundary>
    );
};
export default ConfigurableList;
